import React, { useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import {
  useCreateProjectsIconAllocationMutation,
  useDestroyProjectsIconAllocationMutation,
} from 'components/hooks/projectsIconAllocationMutations';

import { useBreadBoard } from 'components/contexts/Toaster';
import Icon from 'components/projectForm/methodStatement/icons/components/Icon';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function IconGroup({ domainIconGroup, domainIcons, projectId }) {
  const breadBoard = useBreadBoard();

  const createProjectsIconAllocationMutation =
    useCreateProjectsIconAllocationMutation();

  const destroyProjectsIconAllocationMutation =
    useDestroyProjectsIconAllocationMutation();

  const queryClient = useQueryClient();

  const [disabledIconIds, setDisabledIconIds] = useState(new Set());

  const queryParams = { icon_group_id: domainIconGroup.id };

  const queryKey = ['iconAllocations', queryParams, projectId];

  const { data: domainIconAllocations } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await axios.get(
        `/projects/${projectId}/icon_allocations`,
        { params: queryParams },
      );
      return response.data;
    },
  });

  function invalidateIconAllocationsQuery() {
    queryClient.invalidateQueries(queryKey);
  }

  function handleIconCheckboxChange({ iconId, isChecked }) {
    setDisabledIconIds((prevDisabledIcons) => {
      const newDisabledIcons = new Set(prevDisabledIcons);
      newDisabledIcons.add(iconId);
      return newDisabledIcons;
    });

    if (isChecked) {
      createIconAllocation({ iconId });
    } else {
      destroyIconAllocation({ iconId });
    }
  }

  function createIconAllocation({ iconId }) {
    createProjectsIconAllocationMutation.mutate(
      {
        iconAllocationParams: { icon_id: iconId },
        projectId,
      },
      {
        onSuccess: () => {
          invalidateIconAllocationsQuery();

          setDisabledIconIds((prevDisabledIcons) => {
            const newDisabledIcons = new Set(prevDisabledIcons);
            newDisabledIcons.delete(iconId);
            return newDisabledIcons;
          });
        },
        onError: breadBoard.addInedibleToast,
      },
    );
  }

  function destroyIconAllocation({ iconId }) {
    const iconAllocationId = domainIconAllocations.data.find(
      (iconAllocation) => iconAllocation.relationships.icon.data.id === iconId,
    ).id;

    destroyProjectsIconAllocationMutation.mutate(
      {
        id: iconAllocationId,
        projectId,
      },
      {
        onSuccess: () => {
          invalidateIconAllocationsQuery();

          setDisabledIconIds((prevDisabledIcons) => {
            const newDisabledIcons = new Set(prevDisabledIcons);
            newDisabledIcons.delete(iconId);
            return newDisabledIcons;
          });
        },
        onError: breadBoard.addInedibleToast,
      },
    );
  }

  return (
    <div className='row'>
      <div className='col-md-12'>
        <div className='form-container tw-border-grey-100 tw-bg-white'>
          <div className='method_text_header clearfix'>
            <h3 className='tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
              {domainIconGroup.attributes.name}
            </h3>
          </div>
          <fieldset className='inputs method-icons'>
            {domainIcons.map((domainIcon) => (
              <Icon
                domainIcon={domainIcon}
                isChecked={
                  !!domainIconAllocations?.data.find(
                    (iconAllocation) =>
                      iconAllocation.relationships.icon.data.id ===
                      domainIcon.id,
                  )
                }
                isDisabled={disabledIconIds.has(domainIcon.id)}
                key={domainIcon.id}
                onCheckboxChange={(event) => {
                  handleIconCheckboxChange({
                    iconId: domainIcon.id,
                    isChecked: event.target.checked,
                  });
                }}
              />
            ))}
          </fieldset>
        </div>
      </div>
    </div>
  );
}

IconGroup.propTypes = {
  domainIconGroup: resourceShape('iconGroup').isRequired,
  domainIcons: PropTypes.arrayOf(resourceShape('icon')).isRequired,
  projectId: PropTypes.string.isRequired,
};
